<template>
  <form @submit.prevent="queueFile">
    <div class="modal-card">
      <section class="modal-card-body">
        <b-tabs @input="tabChanged" v-model="activeTab">
          <b-tab-item label="Upload file">
            <div style="height:420px">
              <p class="mb-lg">
                To mark invoices as paid in bulk, please upload a CSV file containing the <b>Invoice ID</b> column.
                You can download a <a href="#" @click.prevent="downloadTemplate" class="has-text-info has-text-weight-bold">sample template</a> to get started.
              </p>
              <template v-if="!preferencesLoading">
                <b-message v-if="draftPaymentsOnBulkEnabled" type="is-info" :closable="false" class="mb-lg">
                  <b>Auto-draft payment creation is enabled.</b> Imported invoices will be
                  automatically added to a new or existing draft payment per supplier.
                </b-message>
                <div v-else class="border has-background-white-ter pa-md mb-lg rounded-lg">
                  <p class="has-text-grey is-size-7 mb-sm">
                    Auto-draft payment creation is <b>disabled</b> by default. You can
                    still opt in for this import by checking the box below.
                  </p>
                  <b-field>
                    <b-checkbox v-model="addToDraftPayment">
                      Add imported invoices to a new or existing draft payment per supplier
                    </b-checkbox>
                  </b-field>
                </div>
              </template>
              <CoreUpload @file:uploaded="updateTempfile" @file:reset="resetTempfile"/>
              <div v-if="tempfile !== null" class="border has-background-white-ter pa-lg py-xl mt-4">
                <!-- Ready to schedule -->
                <p v-if="scheduled === false" class="has-text-warning">
                  You may now schedule your file for processing by clicking "Schedule Import" below.
                  Although most files will begin processing immediately, please allow up to
                  10-20 minutes for your import to complete.
                </p>
                <p v-else class="has-text-success">
                  Your file has been added to the queue. Although most files
                  will begin processing immediately, please allow up to
                  10-20 minutes for your import to complete.
                </p>
              </div>
            </div>
          </b-tab-item>
          <b-tab-item label="Check Import Status" :disabled="job_id === null"  >
            <!-- Loaded -->
            <div v-if="!loading && job !== null" style="min-height:420px">
              <!-- Notification -->
              <b-message v-if="job.finish_time !== null && job.error_logs.length === 0" type="is-success">
                Your import is complete with 0 errors. Nice job!
                <p v-if="importedWithDraftPayment" class="mt-sm">
                  Draft payments have been created for imported invoices.
                  <router-link :to="{ name: 'payments.list', query: { preset: 'draft' } }">
                    View draft payments
                  </router-link>
                </p>
              </b-message>
              <b-message v-else-if="job.finish_time !== null && job.error_logs.length > 0"  type="is-warning">
                Your import is complete, but we detected some errors. Please
                check the error log to review the errors. For any rows not
                listed in the error log, you may assume they imported correctly.
              </b-message>
              <b-message v-else type="is-warning">
                Your import is still processing. You might not want to close
                this window until it completes.
                Click <a href="#" @click.prevent="refresh">here</a> to refresh
                the job status.
              </b-message>
              <p class="has-text-weight-bold mb-sm">Job ID #{{ job.id }}</p>
              <div class="columns">
                <div class="column">
                  <div class="columns is-mobile is-vcentered">
                    <div class="column is-narrow">
                      <p class="has-text-weight-bold">Started</p>
                      <p class="has-text-weight-bold">Finished</p>
                    </div>
                    <div class="column">
                      <!-- Start Time -->
                      <p v-if="job.started_at === null" class="has-text-grey">Queued</p>
                      <p v-else>{{ $dates.utc(job.started_at).local().format('MMM D, YYYY @ h:mm A')}}</p>
                      <!-- End Time -->
                      <p v-if="job.finish_time === null">
                        <span> -- </span>
                        <a href="#" @click.prevent="refresh" class="has-text-info is-size-7"> Refresh </a>
                      </p>
                      <p v-else>{{ $dates.utc(job.finish_time).local().format('MMM D, YYYY @ h:mm A')}}</p>
                    </div>
                  </div>
                </div>
                <div class="column is-narrow">
                  <div class="columns is-mobile is-vcentered">
                    <div class="column is-narrow">
                      <p class="has-text-success has-text-weight-bold"># Success</p>
                      <p class="has-text-danger has-text-weight-bold"># Failed</p>
                    </div>
                    <div class="column">
                      <p>
                        <span v-if="job.total_success !== null">{{ job.total_success }}</span>
                        <span v-else>--</span>
                      </p>
                      <p>
                        <span v-if="job.total_failure !== null">{{ job.total_failure }}</span>
                        <span v-else>--</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <b-field v-if="jobErrors.length" label="Error Logs">
                <textarea class="textarea is-small" v-model="jobErrors" disabled style="height:160px"></textarea>
              </b-field>
            </div>
            <!-- Loading -->
            <div v-else style="min-height:360px">
              <loading message="Loading import status..."/>
            </div>
          </b-tab-item>
        </b-tabs>
      </section>
      <footer class="modal-card-foot space-between">
        <div>
          <button class="button" type="button" @click="$parent.close()">Close</button>
          <b-button v-if="job !== null" class="button" type="button" @click="refresh" :loading="loading || refreshing">Refresh</b-button>
        </div>
        <b-button type="is-primary" native-type="submit" :disabled="!ready" :loading="loading || refreshing">Schedule Import</b-button>
      </footer>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import CoreUpload from '@/components/CoreUpload'
export default {
  components: {
    CoreUpload
  },
  computed: {
    ...mapGetters(['context', 'assets']),
    endpoint () {
      return `${this.context.route}/invoices/bulk-payment/`
    },
    preferencesEndpoint () {
      return `${this.context.route}/preferences/?grouping=onboarding`
    },
    json () {
      return {
        tempfile_id: this.tempfile.id,
        add_to_draft_payment: this.addToDraftPayment
      }
    },
    ready () {
      return (!this.scheduling && !this.scheduled && this.tempfile !== null)
    },
    jobErrors () {
      const errors = []
      if (this.job !== null && this.job.error_logs.length > 0) {
        this.job.error_logs.forEach(log => {
          errors.push(log.message)
        })
      }
      return errors.join('\r')
    }
  },
  data () {
    return {
      activeTab: 0,
      scheduling: false,
      scheduled: false,
      tempfile: null,
      job_id: null,
      job: null,
      loading: false,
      refreshing: false,
      draftPaymentsOnBulkEnabled: false,
      addToDraftPayment: false,
      importedWithDraftPayment: false,
      preferencesLoading: true
    }
  },
  methods: {
    queueFile () {
      this.scheduling = true
      this.importedWithDraftPayment = this.addToDraftPayment
      this.$http.put(this.endpoint, this.json).then(response => {
        this.scheduling = false
        this.scheduled = true
        this.$buefy.toast.open({ type: 'is-success', message: 'Import scheduled!' })
        if (response.data.id !== undefined) {
          this.job_id = response.data.id
          this.activeTab = 1
          this.loadJob()
        }
      })
    },
    updateTempfile (file, response) {
      this.tempfile = response
    },
    resetTempfile () {
      this.tempfile = null
    },
    tabChanged (index) {
      if (index === 1) this.loadJob()
    },
    loadJob () {
      this.loading = true
      this.$http.get(`${this.context.route}/jobs/${this.job_id}/`).then(response => {
        this.job = response.data
        this.loading = false
      })
    },
    refresh () {
      this.refreshing = true
      this.$http.get(`${this.context.route}/jobs/${this.job_id}/`).then(response => {
        this.job = response.data
        this.refreshing = false
      })
    },
    downloadTemplate () {
      const link = document.createElement('a')
      link.download = 'ImportInvoicePaymentTemplate.csv'
      const headers = ['Invoice ID']
      const csvContent = headers.join(',')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      link.href = URL.createObjectURL(blob)
      link.click()
    },
    loadDraftPaymentsPreference () {
      this.preferencesLoading = true
      this.$http.get(this.preferencesEndpoint).then(response => {
        const preference = this.$utils.find(response.data.results, ['code', 'add_invoices_to_draft_payment_in_bulk'])
        if (preference && preference.retailer_preference) {
          this.draftPaymentsOnBulkEnabled = preference.retailer_preference.value === '1'
          this.addToDraftPayment = this.draftPaymentsOnBulkEnabled
        }
      }).finally(() => {
        this.preferencesLoading = false
      })
    }
  },
  mounted () {
    this.loadDraftPaymentsPreference()
  }
}
</script>
